import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Magnifier } from '@gravity-ui/icons';
import { NoSearchResults } from '@gravity-ui/illustrations';
import { Button, Icon, TextInput } from '@gravity-ui/uikit';
import { skipToken } from '@reduxjs/toolkit/query';
import type { MRT_ColumnFiltersState, MRT_SortingState } from 'material-react-table';

import { useGetUsersQuery } from 'store/api/private/user-api/admin-api';

import { ACC_LIST_BTN, NO_DATA_ORG_INFO, NO_DATA_USERS, RESET_SEARCH } from '../constants';

import { AdminTable } from './admin-table';
import { AdminTableLoader, AdminTableLoaderAdaptive } from './admin-table-loader';
import { SelectedFilters } from './custom-establishment-filter';
import { AccountAddModal } from './modals';
import { useMediaQuery } from '@mui/material';
import { AdminTableList } from './admin-table-list';
import { ProfileIcon } from 'features/ui-kit/profile-icon';
import styles from '../styles.module.scss';

export const AdminTableAccounts = ({ isMobile }: any) => {
	const [inputValue, setInputValue] = useState<string>('');
	const [pageSize, setPageSize] = useState<number>(20);
	const [pageIndex, setPageIndex] = useState<number>(0);
	const [debouncedInputValue, setDebouncedInputValue] = useState<string>('');
	const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'id', desc: true }]);
	const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

	const isAdaptive = useMediaQuery('(max-width: 900px)');

	const selectedFilters = columnFilters.find((filter) => filter.id === 'establishment.name')?.value as string[] | [];

	const { data, error, isLoading, refetch } = useGetUsersQuery(
		{
			pageNumber: pageIndex,
			pageSize: pageSize,
			sort: sorting[0]?.desc ? 'DESC' : 'ASC',
			sortField: sorting[0]?.id ?? 'id',
			name: debouncedInputValue ? debouncedInputValue : undefined,
			establishmentsIn: selectedFilters,
		} ?? skipToken,
		{ refetchOnMountOrArgChange: true },
	);

	const users = data?.content;

	const totalItems = data?.totalElements;

	useMemo(() => {
		return data?.content;
	}, [data?.content]);

	const handleAddAccount = () => {
		refetch();
	};

	const inputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		setInputValue(newValue.toLowerCase());
	};

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedInputValue(inputValue);
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [inputValue]);

	const removeFilter = (filterToRemove: string) => {
		setColumnFilters((prevFilters) => {
			const updatedFilters = prevFilters.map((filter) =>
				filter.id === 'establishment.name'
					? {
							...filter,
							value: (filter.value as string[]).filter((val) => val !== filterToRemove),
						}
					: filter,
			);
			return updatedFilters.filter((filter) => Array.isArray(filter.value) && filter.value.length > 0);
		});
	};

	const resetFilters = () => {
		setColumnFilters((prevFilters) => prevFilters.filter((filter) => filter.id !== 'establishment.name'));
		setColumnFilters([]);
	};

	return (
		<div className={styles.accounts}>
			{isMobile ? (
				<div className={styles.accounts__title_container}>
					<div className={styles.accounts__title_container__icon}>
						<ProfileIcon />
						<h2 className={styles.accounts__title}>{ACC_LIST_BTN}</h2>
					</div>

					<AccountAddModal onAdd={handleAddAccount} isAdaptive={isAdaptive} isMobile={isMobile} />
				</div>
			) : (
				<h2 className={styles.accounts__title}>{ACC_LIST_BTN}</h2>
			)}

			<div className={styles.accounts__search}>
				<TextInput
					className={styles.accounts__search__input}
					placeholder="Поиск по ФИО"
					size="l"
					startContent={<Icon data={Magnifier} size={16} />}
					hasClear
					value={inputValue}
					onChange={inputChange}
				/>

				{isMobile ? '' : <AccountAddModal onAdd={handleAddAccount} isAdaptive={isAdaptive} />}
			</div>

			<SelectedFilters
				selectedFilters={selectedFilters}
				removeFilter={removeFilter}
				resetFilters={resetFilters}
				isMobile={isMobile}
			/>

			<div className={styles.accounts}>
				<div className={styles.accounts__table}>
					{isLoading && <>{isAdaptive ? <AdminTableLoaderAdaptive /> : <AdminTableLoader />}</>}

					{!isLoading && users?.length !== 0 && (
						<>
							{isAdaptive ? (
								<AdminTableList
									data={users || []}
									onAdd={handleAddAccount}
									pageSize={pageSize}
									setPageSize={setPageSize}
									pageIndex={pageIndex}
									setPageIndex={setPageIndex}
									totalItems={totalItems}
									sorting={sorting}
									setSorting={setSorting}
									columnFilters={columnFilters}
									setColumnFilters={setColumnFilters}
									selectedFilters={selectedFilters}
									isMobile={isMobile}
									resetFilters={resetFilters}
								/>
							) : (
								<div className={styles.accounts__table__main}>
									<AdminTable
										data={users || []}
										onAdd={handleAddAccount}
										pageSize={pageSize}
										setPageSize={setPageSize}
										pageIndex={pageIndex}
										setPageIndex={setPageIndex}
										totalItems={totalItems}
										sorting={sorting}
										setSorting={setSorting}
										columnFilters={columnFilters}
										setColumnFilters={setColumnFilters}
										selectedFilters={selectedFilters}
									/>
								</div>
							)}
						</>
					)}

					{!isLoading && users?.length == 0 && (
						<div className={styles.accounts__table__main__empty}>
							<NoSearchResults />
							<h3>{NO_DATA_USERS}</h3>
							<p>{NO_DATA_ORG_INFO}</p>
							<Button onClick={() => setInputValue('')} view="normal" size="l">
								{RESET_SEARCH}
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
